/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  message,
  Table,
  Button,
  Tag,
  Modal,
  Alert,
  Dropdown,
  Input,
  Select,
  DatePicker,
} from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  MoreOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { reservationsApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";
import ResponsiveTable from "../../_components/ResponsiveTable";

const STATUS_COLORS = {
  confirmed: "green",
  cancelled: "red",
  refunded: "orange",
  checked_in: "volcano",
};

// ─────────────────────────────────────────────────────────────────────────────
const ReservationsList = () => {
  const history = useHistory();

  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({ page: 1, limit: 10 });
  const [actionLoading, setActionLoading] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);

  // Cancel modal
  const [cancelModal, setCancelModal] = useState({ visible: false, id: null });
  // Refund modal
  const [refundModal, setRefundModal] = useState({ visible: false, id: null });
  // Delete modal
  const [deleteModal, setDeleteModal] = useState({ visible: false, id: null });

  // Load user data
  useEffect(() => {
    const loadUser = () => {
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const userData = JSON.parse(userStr);

          // Extract permissions - handle both string array and object array
          let permissions = [];

          if (userData.permissions && Array.isArray(userData.permissions)) {
            permissions = userData.permissions;
          } else if (userData.role && userData.role.permissions) {
            permissions = userData.role.permissions;
          }

          // Normalize to strings
          const normalizedPermissions = permissions.map((p) =>
            typeof p === "string" ? p : p.name,
          );

          setUserPermissions(normalizedPermissions);
          console.log(
            "ReservationsList - User permissions:",
            normalizedPermissions,
          );
        }
      } catch (error) {
        console.error("Error loading user:", error);
      } finally {
        setIsCheckingPermission(false);
      }
    };
    loadUser();
  }, []);

  // Check permissions using the standardized hasPermission utility
  const canViewReservations = useMemo(() => {
    return hasPermission(userPermissions, "reservations.view");
  }, [userPermissions]);

  const canCreateReservations = useMemo(() => {
    return hasPermission(userPermissions, "reservations.create");
  }, [userPermissions]);

  const canEditReservations = useMemo(() => {
    return hasPermission(userPermissions, "reservations.edit");
  }, [userPermissions]);

  const canDeleteReservations = useMemo(() => {
    return hasPermission(userPermissions, "reservations.delete");
  }, [userPermissions]);

  const canRefundReservations = useMemo(() => {
    return hasPermission(userPermissions, "reservations.refund");
  }, [userPermissions]);

  useEffect(() => {
    if (!isCheckingPermission && canViewReservations) {
      fetchReservations();
    }
  }, [filters, canViewReservations, isCheckingPermission]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const r = await reservationsApi.getAll(filters);
      console.log("Fetched reservations:", r);
      setReservations(r.data || []);
      setTotalItems(r.total || (r.data || []).length);
    } catch (error) {
      console.error("Error fetching reservations:", error);
      if (error.response?.status === 403) {
        message.error("You don't have permission to view reservations");
      } else {
        message.error("Failed to load reservations");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Cancel ─────────────────────────────────────────────────────────────────
  const handleCancel = async () => {
    if (!canEditReservations) {
      message.error("You don't have permission to cancel reservations");
      return;
    }
    try {
      setActionLoading(true);
      await reservationsApi.cancel(cancelModal.id);
      message.success("Reservation cancelled");
      setCancelModal({ visible: false, id: null });
      fetchReservations();
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to cancel reservations");
      } else {
        message.error(e.response?.data?.message || "Failed to cancel");
      }
    } finally {
      setActionLoading(false);
    }
  };

  // ── Refund ─────────────────────────────────────────────────────────────────
  const handleRefund = async () => {
    if (!canRefundReservations) {
      message.error("You don't have permission to refund reservations");
      return;
    }
    try {
      setActionLoading(true);
      await reservationsApi.refund(refundModal.id);
      message.success("Reservation marked as refunded");
      setRefundModal({ visible: false, id: null });
      fetchReservations();
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to refund reservations");
      } else {
        message.error(e.response?.data?.message || "Failed to refund");
      }
    } finally {
      setActionLoading(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!canDeleteReservations) {
      message.error("You don't have permission to delete reservations");
      return;
    }
    try {
      setActionLoading(true);
      await reservationsApi.remove(deleteModal.id);
      message.success("Reservation permanently deleted");
      setDeleteModal({ visible: false, id: null });
      fetchReservations();
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to delete reservations");
      } else {
        message.error(e.response?.data?.message || "Failed to delete");
      }
    } finally {
      setActionLoading(false);
    }
  };

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Customer",
      key: "customer",
      render: (_, r) => {
        const name = r.user
          ? `${r.user.firstName} ${r.user.lastName}`
          : r.guestName || "Guest";
        const email = r.user?.email || r.guestEmail || "-";
        return (
          <div>
            <strong>{name}</strong>
            <div className="small text-muted">{email}</div>
          </div>
        );
      },
    },
    {
      title: "Trip / Date",
      key: "trip",
      render: (_, r) => (
        <div>
          <strong>{r.tripDate?.trip?.name || "-"}</strong>
          <div className="small text-muted">{r.tripDate?.date || "-"}</div>
        </div>
      ),
    },
    {
      title: "Deck",
      key: "deck",
      render: (_, r) => r.deck?.name || "-",
      width: 120,
    },
    {
      title: "Seats",
      key: "seats",
      align: "center",
      render: (_, r) => (
        <div style={{ fontSize: 13 }}>
          <strong>{r.adultSeats} A</strong>
          {r.childSeats > 0 && (
            <span style={{ marginLeft: 4, color: "#9ca3af" }}>
              / {r.childSeats} C
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Total",
      dataIndex: "totalPrice",
      key: "total",
      render: (v) => `EGP ${Number(v).toLocaleString()}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => (
        <Tag color={STATUS_COLORS[s]}>{s?.replace("_", " ").toUpperCase()}</Tag>
      ),
    },
    {
      title: "Type",
      dataIndex: "isAdminReservation",
      key: "type",
      render: (v) => (
        <Tag color={v ? "purple" : "default"}>{v ? "Admin" : "Customer"}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, r) => {
        const items = [
          {
            key: "view",
            label: "View",
            icon: <EyeOutlined />,
            onClick: () => history.push(`/reservations/${r.id}`),
          },
        ];

        if (r.status === "confirmed" && canEditReservations) {
          items.push({ type: "divider" });
          items.push({
            key: "cancel",
            label: "Cancel",
            icon: <CloseCircleOutlined />,
            danger: true,
            onClick: () => setCancelModal({ visible: true, id: r.id }),
          });
        }

        if (r.status === "confirmed" && canRefundReservations) {
          items.push({
            key: "refund",
            label: "Mark Refunded",
            icon: <DollarOutlined />,
            onClick: () => setRefundModal({ visible: true, id: r.id }),
          });
        }

        if (canDeleteReservations) {
          items.push({ type: "divider" });
          items.push({
            key: "delete",
            label: "Delete Permanently",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => setDeleteModal({ visible: true, id: r.id }),
          });
        }

        // If no actions available (only view), show view button directly
        if (items.length === 1) {
          return (
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => history.push(`/reservations/${r.id}`)}
            />
          );
        }

        return (
          <Dropdown
            menu={{ items }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  // Show loading while checking permissions
  if (isCheckingPermission) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Checking permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If no permission, show access denied
  if (!canViewReservations) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              type="error"
              message="Access Denied"
              description={
                <div>
                  <p>You don't have permission to view all reservations.</p>
                  <p className="mt-2">
                    <strong>Required permission:</strong> reservations.view
                  </p>
                  <p className="mt-2">
                    <strong>Your permissions:</strong>{" "}
                    {userPermissions.length > 0
                      ? userPermissions.join(", ")
                      : "None"}
                  </p>
                </div>
              }
              showIcon
              action={
                <Button
                  size="small"
                  type="primary"
                  onClick={() => history.push("/reservations/my")}
                  style={{ backgroundColor: "#7B4D12", borderColor: "#7B4D12" }}
                >
                  Go to My Reservations
                </Button>
              }
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Render main content ─────────────────────────────────────────────────
  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0 premium-card">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>All Reservations</h5>
                  {canCreateReservations && (
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                      icon={<PlusOutlined />}
                      onClick={() => history.push("/reservations/add")}
                    >
                      New Reservation
                    </Button>
                  )}
                </div>
              </div>

              {/* Search + filters */}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  margin: "8px 0 20px",
                }}
              >
                <Input.Search
                  placeholder="Search name, email, booking #, or ticket…"
                  allowClear
                  style={{ width: 320 }}
                  onSearch={(v) =>
                    setFilters((f) => ({ ...f, page: 1, search: v || undefined }))
                  }
                />
                <Select
                  placeholder="Status"
                  allowClear
                  style={{ width: 160 }}
                  value={filters.status}
                  onChange={(v) =>
                    setFilters((f) => ({ ...f, page: 1, status: v }))
                  }
                  options={[
                    { value: "pending", label: "Pending" },
                    { value: "confirmed", label: "Confirmed" },
                    { value: "cancelled", label: "Cancelled" },
                    { value: "refunded", label: "Refunded" },
                    { value: "checked_in", label: "Checked in" },
                  ]}
                />
                <DatePicker.RangePicker
                  onChange={(vals) =>
                    setFilters((f) => ({
                      ...f,
                      page: 1,
                      startDate: vals?.[0]?.startOf("day").toISOString(),
                      endDate: vals?.[1]?.endOf("day").toISOString(),
                      dateField: "created",
                    }))
                  }
                />
              </div>

              {!canEditReservations &&
                !canDeleteReservations &&
                !canRefundReservations && (
                  <Alert
                    message="View Only Mode"
                    description="You have view-only access to reservations. To perform actions like cancel, refund, or delete, please contact an administrator."
                    type="info"
                    showIcon
                    className="mb-4"
                  />
                )}

              <ResponsiveTable
                columns={columns}
                dataSource={reservations}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: filters.page,
                  pageSize: filters.limit,
                  total: totalItems,
                  showSizeChanger: true,
                  showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} reservations`,
                }}
                onChange={(p) =>
                  setFilters((f) => ({
                    ...f,
                    page: p.current,
                    limit: p.pageSize,
                  }))
                }
                scroll={{ x: true }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      <Modal
        title="Cancel Reservation"
        open={cancelModal.visible}
        onOk={handleCancel}
        onCancel={() => setCancelModal({ visible: false, id: null })}
        confirmLoading={actionLoading}
        okText="Cancel Reservation"
        okType="danger"
      >
        <Alert
          message="This reservation will be marked as cancelled. The record is kept."
          type="warning"
          showIcon
          className="mb-3"
        />
        <p>
          Cancel reservation <code>#{cancelModal.id?.slice(0, 8)}…</code>?
        </p>
      </Modal>

      {/* Refund Modal */}
      <Modal
        title="Mark as Refunded"
        open={refundModal.visible}
        onOk={handleRefund}
        onCancel={() => setRefundModal({ visible: false, id: null })}
        confirmLoading={actionLoading}
        okText="Mark Refunded"
      >
        <p>
          Mark reservation <code>#{refundModal.id?.slice(0, 8)}…</code> as
          refunded?
        </p>
      </Modal>

      {/* Delete Modal */}
      <Modal
        title={
          <span>
            <DeleteOutlined style={{ color: "#ef4444", marginRight: 8 }} />
            Delete Permanently
          </span>
        }
        open={deleteModal.visible}
        onOk={handleDelete}
        onCancel={() => setDeleteModal({ visible: false, id: null })}
        confirmLoading={actionLoading}
        okText="Delete Forever"
        okType="danger"
      >
        <Alert
          message="This action cannot be undone."
          description="The reservation record, its ticket, and all related data will be permanently removed from the database."
          type="error"
          showIcon
          className="mb-3"
        />
        <p>
          Delete reservation <code>#{deleteModal.id?.slice(0, 8)}…</code>?
        </p>
      </Modal>
    </div>
  );
};

export default ReservationsList;
